import { delay } from "./common";
import { getAsyncInferenceApiUrl } from "../../constants/ApiUrl";
import { EndpointType } from "../../constants/Endpoints";
import { UPLOAD_DELAY_MS } from "../../constants/upload";
import HttpException from "../../errors/HttpException";
import { ImageUploaderResponseSingle } from "../../types/upload";
import { createInferenceFormData } from "../../utils/api";
import { assertFilesSelected } from "../../utils/file";
import { endpointStore } from "../../utils/mobx/EndpointStore";
import { envVarStore } from "../../utils/mobx/EnvVarStore";

export const imageAsyncUploader = async (
  selectedFiles: File[],
  tenantId: string,
  url: string,
  type: string,
  serviceName: string,
): Promise<string[]> => {
  assertFilesSelected(selectedFiles);

  const { endpointType } = endpointStore;
  const { backendUrl, multifileSingleRequest } = envVarStore;
  const apiUrl = getAsyncInferenceApiUrl(backendUrl, endpointType);

  if (multifileSingleRequest === "false") {
    return await uploadFilesSequentially(
      selectedFiles,
      url,
      type,
      serviceName,
      endpointType,
      tenantId,
      apiUrl,
    );
  } else {
    return await uploadFilesAsBatch(
      selectedFiles,
      url,
      type,
      serviceName,
      endpointType,
      tenantId,
      apiUrl,
    );
  }
};

const uploadFilesSequentially = async (
  files: File[],
  url: string,
  type: string,
  serviceName: string,
  endpointType: EndpointType,
  tenantId: string,
  apiUrl: string,
): Promise<string[]> => {
  const requestIds: string[] = [];

  for (const userFile of files) {
    const requestId = await uploadSingleFile(
      userFile,
      url,
      type,
      serviceName,
      endpointType,
      tenantId,
      apiUrl,
    );
    requestIds.push(requestId);
    await delay(UPLOAD_DELAY_MS);
  }

  return requestIds;
};

const uploadFilesAsBatch = async (
  files: File[],
  url: string,
  type: string,
  serviceName: string,
  endpointType: EndpointType,
  tenantId: string,
  apiUrl: string,
): Promise<string[]> => {
  const requestId = await uploadSingleFile(
    files,
    url,
    type,
    serviceName,
    endpointType,
    tenantId,
    apiUrl,
  );
  return [requestId];
};

const uploadSingleFile = async (
  fileOrFiles: File | File[],
  url: string,
  type: string,
  serviceName: string,
  endpointType: EndpointType,
  tenantId: string,
  apiUrl: string,
): Promise<string> => {
  const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
  const fd = createInferenceFormData(
    files,
    url,
    type,
    serviceName,
    endpointType,
  );

  const response = await fetch(apiUrl, {
    method: "POST",
    headers: { "X-Upstage-Tenant-ID": tenantId },
    body: fd,
  });

  if (!response.ok) {
    throw HttpException;
  }

  const responseData = (await response.json()) as ImageUploaderResponseSingle;
  return responseData.requestId;
};
